import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { PracticeSessionItem, SessionSource } from '@/types/seeds';
import {
  formatDateTime,
  formatSessionSource,
  formatSessionStatus,
} from '@/lib/utils';

interface SessionsTableProps {
  sessions: PracticeSessionItem[];
  hideStudent?: boolean;
}

const statusVariant = (status: string) => {
  switch (status) {
    case 'done':
      return 'default';
    case 'skipped':
      return 'secondary';
    case 'pending':
      return 'outline';
    default:
      return 'outline';
  }
};

const sourceVariant = (source: SessionSource) => {
  switch (source) {
    case 'assignment':
      return 'default';
    case 'ai':
      return 'secondary';
    default:
      return 'outline';
  }
};

export function SessionsTable({ sessions, hideStudent = false }: SessionsTableProps) {
  const colSpan = hideStudent ? 6 : 7;
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {!hideStudent && <TableHead>Student</TableHead>}
            <TableHead>Practice</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Rating</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} className="text-center text-muted-foreground">
                No practice sessions found.
              </TableCell>
            </TableRow>
          ) : (
            sessions.map((s) => (
              <TableRow key={s.id}>
                {!hideStudent && (
                  <TableCell className="font-medium">{s.student_name}</TableCell>
                )}
                <TableCell>
                  <div className="flex flex-col">
                    <span>{s.practice_title}</span>
                    {s.problem_category && (
                      <span className="text-xs text-muted-foreground">
                        {s.problem_category}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={sourceVariant(s.source)}>
                    {formatSessionSource(s.source)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(s.status)}>
                    {formatSessionStatus(s.status)}
                  </Badge>
                </TableCell>
                <TableCell>{s.difficulty_rating ?? '—'}</TableCell>
                <TableCell className="max-w-[200px] truncate">
                  {s.notes ?? '—'}
                </TableCell>
                <TableCell>{formatDateTime(s.created_at)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
